import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getExplainerVideo, setExplainerVideoLink, uploadExplainerVideo, deleteExplainerVideo, errMsg,
} from '../lib/api';
import { toEmbedUrl, isDirectVideo } from '../lib/video';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { PlayCircle, Video, Pencil, Trash2, Link2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const SECTION_LABELS = {
  'dashboard': 'Dashboard',
  'risk-overview': 'Risk Overview',
  'assessments': 'Safety Assessment',
  'incidents': 'Incidents',
  'policies': 'Safety Policies',
  'loss-runs': 'Loss Runs',
  'organizations': 'Organizations',
  'users': 'User Management',
  'broker': 'Broker Dashboard',
  'partners': 'Partner Marketplace',
  'guidance': 'Guidance Library',
  'alerts': 'Alerts',
  'settings': 'Settings',
  'network-intelligence': 'Network Intelligence',
  'training': 'Training',
};

export const ExplainerVideo = ({ section }) => {
  const { isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  // Manage form state
  const [linkUrl, setLinkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchVideo = useCallback(async () => {
    try {
      const res = await getExplainerVideo(section);
      setVideo(res.data || null);
    } catch {
      setVideo(null);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => { fetchVideo(); }, [fetchVideo]);

  const handleSaveLink = async () => {
    if (!linkUrl.trim()) { toast.error('Please paste a YouTube or Vimeo link'); return; }
    setSaving(true);
    try {
      const res = await setExplainerVideoLink(section, linkUrl.trim(), title.trim());
      setVideo(res.data);
      toast.success('Explainer video saved');
      setOpen(false); setLinkUrl(''); setTitle('');
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save video'));
    } finally { setSaving(false); }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please choose an MP4 file'); return; }
    setSaving(true);
    try {
      const res = await uploadExplainerVideo(section, file, title.trim());
      setVideo(res.data);
      toast.success('Explainer video uploaded');
      setOpen(false); setFile(null); setTitle('');
    } catch (e) {
      toast.error(errMsg(e, 'Failed to upload video'));
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await deleteExplainerVideo(section);
      setVideo(null);
      toast.success('Explainer video removed');
      setOpen(false);
    } catch {
      toast.error('Failed to remove video');
    } finally { setSaving(false); }
  };

  if (loading) return null;
  // Non-admins with no video see nothing
  if (!video && !isAdmin) return null;

  const embedUrl = video ? toEmbedUrl(video.video_url) : null;
  const direct = video ? isDirectVideo(video.video_url) : false;

  const manageDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`explainer-manage-${section}`}
          className="gap-1.5"
        >
          {video ? <Pencil className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />}
          {video ? 'Edit video' : 'Add explainer video'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Explainer Video — {SECTION_LABELS[section] || section}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="link" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="link" data-testid="explainer-tab-link">
              <Link2 className="w-4 h-4 mr-1.5" /> Paste link
            </TabsTrigger>
            <TabsTrigger value="upload" data-testid="explainer-tab-upload">
              <Upload className="w-4 h-4 mr-1.5" /> Upload MP4
            </TabsTrigger>
          </TabsList>
          <TabsContent value="link" className="space-y-3 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor={`evtitle-${section}`}>Title (optional)</Label>
              <Input id={`evtitle-${section}`} value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. How to use this section" data-testid="explainer-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`evlink-${section}`}>YouTube / Vimeo URL</Label>
              <Input id={`evlink-${section}`} value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..." data-testid="explainer-link-input" />
            </div>
            <DialogFooter>
              <Button onClick={handleSaveLink} disabled={saving} data-testid="explainer-save-link-btn">
                {saving ? 'Saving…' : 'Save video'}
              </Button>
            </DialogFooter>
          </TabsContent>
          <TabsContent value="upload" className="space-y-3 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor={`evtitle2-${section}`}>Title (optional)</Label>
              <Input id={`evtitle2-${section}`} value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. How to use this section" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`evfile-${section}`}>MP4 file (max 200MB)</Label>
              <Input id={`evfile-${section}`} type="file" accept=".mp4,.webm,.mov,.m4v"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="explainer-file-input" />
            </div>
            <DialogFooter>
              <Button onClick={handleUpload} disabled={saving} data-testid="explainer-upload-btn">
                {saving ? 'Uploading…' : 'Upload & save'}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
        {video && (
          <div className="pt-2 border-t mt-1">
            <Button variant="ghost" size="sm" onClick={handleRemove} disabled={saving}
              className="text-destructive hover:text-destructive" data-testid="explainer-remove-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Remove current video
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // Empty state for admins only
  if (!video) {
    return (
      <div className="mb-6 rounded-lg border border-dashed border-border bg-secondary/30 px-4 py-3 flex items-center justify-between gap-3"
        data-testid={`explainer-slot-${section}`}>
        <div className="flex items-center gap-2.5 text-muted-foreground">
          <Video className="w-5 h-5" />
          <span className="text-sm">No explainer video yet for this section. Add one for your orgs.</span>
        </div>
        {manageDialog}
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-border bg-card overflow-hidden"
      data-testid={`explainer-slot-${section}`}>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border bg-secondary/30">
        <button
          onClick={() => setShowPlayer((s) => !s)}
          className="flex items-center gap-2.5 text-left group"
          data-testid={`explainer-toggle-${section}`}
        >
          <PlayCircle className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" />
          <div>
            <p className="text-sm font-medium leading-tight">
              {video.title || `${SECTION_LABELS[section] || 'Section'} Walkthrough`}
            </p>
            <p className="text-xs text-muted-foreground">
              {showPlayer ? 'Click to hide' : 'Watch a short explainer'}
            </p>
          </div>
        </button>
        {isAdmin && manageDialog}
      </div>
      {showPlayer && (
        <div className="relative w-full bg-black aspect-video" data-testid={`explainer-player-${section}`}>
          <button
            onClick={() => setShowPlayer(false)}
            className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
            aria-label="Close video"
          >
            <X className="w-4 h-4" />
          </button>
          {direct ? (
            <video src={embedUrl} controls className="w-full h-full" data-testid={`explainer-video-el-${section}`}>
              <track kind="captions" />
            </video>
          ) : (
            <iframe
              src={embedUrl}
              title={video.title || 'Explainer video'}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ExplainerVideo;
